import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Edit3, Save, RefreshCw, Plus, Trash2, Megaphone, ImagePlus, X } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { confirmAction } from '@/lib/confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const defaultContent = {
  hero_title: 'A.C.C.T',
  hero_subtitle: 'Armor Critical Competitive Tournament',
  hero_tagline: 'Where champions are forged. Join the ultimate competitive gaming experience.',
  marquee_text: '',
  marquee_enabled: 'true',
  marquee_repeat: '3',
  features_title: 'Compete at the Highest Level',
  features_subtitle: 'Why Join A.C.C.T',
  feature_1_title: 'Competitive Play',
  feature_1_desc: 'Battle for glory in official tournaments',
  feature_2_title: 'Team Registration',
  feature_2_desc: 'Build your squad and compete',
  feature_3_title: 'Regular Events',
  feature_3_desc: 'Weekly matches and seasonal championships',
  teams_title: 'Competing Squads',
  teams_subtitle: 'Registered Teams',
  rules_title: 'Tournament Rules',
  rules_subtitle: 'Official Guidelines',
  bracket_title: 'Tournament Bracket',
  footer_copyright: '2026 A.C.C.T. All rights reserved.',
};

export default function AdminEditPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnn, setNewAnn] = useState({ title: '', content: '' });
  const [editingAnn, setEditingAnn] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchContent();
    }
  }, [isAdmin, authLoading, navigate]);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContent({ ...defaultContent, ...data });
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent({ ...content, [key]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/content/bulk`, content);
      toast.success('Content saved successfully!');
    } catch (err) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(defaultContent);
    toast.info('Content reset to defaults (not saved)');
  };

  const fetchAnnouncements = async () => {
    try {
      const { data } = await axios.get(`${API}/announcements`);
      setAnnouncements(data);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnn.title || !newAnn.content) return;
    try {
      await axios.post(`${API}/announcements`, newAnn);
      toast.success('Announcement posted!');
      setNewAnn({ title: '', content: '' });
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (annId) => {
    try {
      await axios.delete(`${API}/announcements/${annId}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleAnnouncementImageUpload = async (annId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/announcements/${annId}/image`, fd);
      toast.success('Image uploaded');
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleAnnouncementImageRemove = async (annId) => {
    try {
      await axios.delete(`${API}/admin/announcements/${annId}/image`);
      toast.success('Image removed');
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  const startEditAnnouncement = (ann) => {
    setEditingAnn(ann);
    setNewAnn({ title: ann.title, content: ann.content });
  };

  const handleUpdateAnnouncement = async (e) => {
    e.preventDefault();
    if (!editingAnn) return;
    try {
      await axios.put(`${API}/announcements/${editingAnn.id}`, newAnn);
      toast.success('Announcement updated!');
      setEditingAnn(null);
      setNewAnn({ title: '', content: '' });
      fetchAnnouncements();
    } catch (err) {
      toast.error('Failed to update announcement');
    }
  };

  const cancelEditAnnouncement = () => {
    setEditingAnn(null);
    setNewAnn({ title: '', content: '' });
  };

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  const sections = [
    {
      title: 'Hero Section',
      fields: [
        { key: 'hero_title', label: 'Title', type: 'input' },
        { key: 'hero_subtitle', label: 'Subtitle', type: 'input' },
        { key: 'hero_tagline', label: 'Tagline', type: 'textarea' },
        { key: 'marquee_text', label: 'Scrolling Marquee Text (leave empty for auto team names)', type: 'input' },
        { key: 'marquee_repeat', label: 'Times to display per round (1-20)', type: 'input' },
        { key: 'marquee_enabled', label: 'Enable scrolling text', type: 'checkbox' },
      ]
    },
    {
      title: 'Features Section',
      fields: [
        { key: 'features_subtitle', label: 'Section Label', type: 'input' },
        { key: 'features_title', label: 'Section Title', type: 'input' },
        { key: 'feature_1_title', label: 'Feature 1 Title', type: 'input' },
        { key: 'feature_1_desc', label: 'Feature 1 Description', type: 'input' },
        { key: 'feature_2_title', label: 'Feature 2 Title', type: 'input' },
        { key: 'feature_2_desc', label: 'Feature 2 Description', type: 'input' },
        { key: 'feature_3_title', label: 'Feature 3 Title', type: 'input' },
        { key: 'feature_3_desc', label: 'Feature 3 Description', type: 'input' },
      ]
    },
    {
      title: 'Teams Section',
      fields: [
        { key: 'teams_subtitle', label: 'Section Label', type: 'input' },
        { key: 'teams_title', label: 'Section Title', type: 'input' },
      ]
    },
    {
      title: 'Rules Page',
      fields: [
        { key: 'rules_subtitle', label: 'Section Label', type: 'input' },
        { key: 'rules_title', label: 'Page Title', type: 'input' },
      ]
    },
    {
      title: 'Bracket Page',
      fields: [
        { key: 'bracket_title', label: 'Page Title', type: 'input' },
      ]
    },
    {
      title: 'Footer',
      fields: [
        { key: 'footer_copyright', label: 'Copyright Text', type: 'input' },
      ]
    },
  ];

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="admin-edit-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Edit3 className="w-8 h-8 text-cyan-400" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white">
                Edit Site Content
              </h1>
            </div>
            <p className="text-zinc-500">
              Customize all text content on the website
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-zinc-800 text-zinc-400 hover:text-white rounded-none"
              data-testid="reset-content-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
              data-testid="save-content-button"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Save Changes
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <div key={section.title} className="bg-[#121212] border border-zinc-800 p-6">
              <h2 className="font-heading text-lg font-bold text-white uppercase mb-6">
                {section.title}
              </h2>
              <div className="space-y-4">
                {section.fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                      {field.label}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={content[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none font-body"
                        data-testid={`edit-${field.key}`}
                      />
                    ) : field.type === 'checkbox' ? (
                      <div className="flex items-center gap-3 h-12">
                        <button
                          type="button"
                          onClick={() => handleChange(field.key, content[field.key] === 'true' ? 'false' : 'true')}
                          className={`w-10 h-6 rounded-full transition-colors relative ${content[field.key] === 'true' ? 'bg-cyan-400' : 'bg-zinc-800'}`}
                          data-testid={`edit-${field.key}`}
                        >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${content[field.key] === 'true' ? 'left-5' : 'left-1'}`} />
                        </button>
                        <span className={`text-sm ${content[field.key] === 'true' ? 'text-cyan-400' : 'text-zinc-600'}`}>
                          {content[field.key] === 'true' ? 'On' : 'Off'}
                        </span>
                      </div>
                    ) : (
                      <Input
                        value={content[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 font-body"
                        data-testid={`edit-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Announcements Management */}
          <div className="bg-[#121212] border border-zinc-800 p-6" data-testid="announcements-management">
            <div className="flex items-center gap-3 mb-6">
              <Megaphone className="w-5 h-5 text-yellow-500" />
              <h2 className="font-heading text-lg font-bold text-white uppercase">
                Announcements
              </h2>
            </div>

            {/* Create / Edit form */}
            <form onSubmit={editingAnn ? handleUpdateAnnouncement : handleCreateAnnouncement} className="space-y-3 mb-6 p-4 bg-black border border-zinc-800">
              {editingAnn && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-cyan-400 text-xs uppercase font-bold">Editing Announcement</span>
                  <button type="button" onClick={cancelEditAnnouncement} className="text-zinc-500 hover:text-white text-xs">Cancel</button>
                </div>
              )}
              <Input
                value={newAnn.title}
                onChange={(e) => setNewAnn({ ...newAnn, title: e.target.value })}
                placeholder="Announcement title"
                required
                className="bg-[#050505] border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-10 focus:border-cyan-400"
                data-testid="announcement-title-input"
              />
              <Textarea
                value={newAnn.content}
                onChange={(e) => setNewAnn({ ...newAnn, content: e.target.value })}
                placeholder="Announcement message..."
                required
                className="bg-[#050505] border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[60px] focus:border-cyan-400 resize-none"
                data-testid="announcement-content-input"
              />
              <Button
                type="submit"
                className="bg-yellow-500 text-black font-bold uppercase tracking-wide hover:bg-yellow-400 rounded-none"
                data-testid="post-announcement-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                {editingAnn ? 'Update Announcement' : 'Post Announcement'}
              </Button>
            </form>

            {/* Existing announcements */}
            {announcements.length === 0 ? (
              <p className="text-zinc-500 text-sm">No announcements yet.</p>
            ) : (
              <div className="space-y-3">
                {announcements.map((ann) => (
                  <div
                    key={ann.id}
                    className="bg-black border-l-4 border-l-yellow-500 border-y border-r border-y-zinc-800 border-r-zinc-800 p-4 flex items-start justify-between gap-3"
                    data-testid={`announcement-item-${ann.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <h4 className="text-white font-medium">{ann.title}</h4>
                      <p className="text-zinc-500 text-sm mt-1 whitespace-pre-wrap">{ann.content}</p>
                      {ann.image_url && (
                        <div className="mt-2 inline-block relative">
                          <img src={`${BASE}${ann.image_url}`} alt="" className="max-h-32 w-auto object-contain border border-zinc-800" data-testid={`ann-img-preview-${ann.id}`} />
                          <button
                            onClick={() => handleAnnouncementImageRemove(ann.id)}
                            className="absolute top-1 right-1 bg-black/70 text-red-400 hover:text-red-300 p-1"
                            title="Remove image"
                            data-testid={`remove-ann-img-${ann.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <p className="text-zinc-700 text-xs mt-2">{new Date(ann.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <label className="text-zinc-500 hover:text-yellow-400 transition-colors cursor-pointer" title="Upload image" data-testid={`upload-ann-img-${ann.id}`}>
                        <ImagePlus className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleAnnouncementImageUpload(ann.id, e.target.files?.[0])}
                        />
                      </label>
                      <button
                        onClick={() => startEditAnnouncement(ann)}
                        className="text-zinc-500 hover:text-cyan-400 transition-colors"
                        data-testid={`edit-announcement-${ann.id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAnnouncement(ann.id)}
                        className="text-zinc-500 hover:text-red-500 transition-colors"
                        data-testid={`delete-announcement-${ann.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Save Button (bottom) */}
        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none px-8"
            data-testid="save-content-button-bottom"
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </main>
  );
}
